import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Box, Accordion, AccordionSummary, AccordionDetails, Typography, useTheme } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { useEffectivePermissions, toSet, canEmitirBPe } from "../../hooks/useEffectivePermissions";
import { useWorkspaceSegment } from "../../hooks/useWorkspaceSegment";
import { WorkspaceSegments } from "../../constants/workspaceSegments";
import CustomerDetails from "./CustomerDetails";
import RequestDetails from "./RequestDetails";
import Products from "./Products";
import History from "./History";
import AiInsights from "./AiInsights";
import PassagensBPe from "./PassagensBPe";

const ConversationDetails = ({
  selectedConversation,
  onSelectedConversationUpdate,
  full = false,
  aiInsights,
  isLoadingInsights,
  aiInsightsError,
  onLoadAiInsights,
  onRefetchConversationDetails,
}) => {
  const { t } = useTranslation("chat");
  const theme = useTheme();
  const [insightsExpanded, setInsightsExpanded] = useState(false);
  const { data: permissionsData } = useEffectivePermissions();
  const { segment, isLoading: isLoadingSegment } = useWorkspaceSegment();
  const scopesSet = toSet(permissionsData?.scopes);
  const canShowBPe = !isLoadingSegment && segment === WorkspaceSegments.PassengerTransport && canEmitirBPe(scopesSet);
  const canShowProducts = !isLoadingSegment && segment !== WorkspaceSegments.PassengerTransport;

  useEffect(() => {
    // Sempre começa fechado ao trocar de conversa
    setInsightsExpanded(false);
  }, [selectedConversation?.id]);

  const formatDate = (date) => {
    const formattedDate = new Date(date);
    return formattedDate.toLocaleDateString() + " " + formattedDate.toLocaleTimeString();
  };

  const handleItemClick = (id) => {
    const url = `/chat/${id}`;
    window.location.href = url;
  };

  return (
    <Box
      p={full ? 1 : 1.5}
      display="flex"
      flexDirection="column"
      gap={1}
      overflow="auto"
      className="sidebar-scroll"
      sx={{
        backgroundColor: theme.palette.background.default,
        // Em modo "full" (mobile), preenche apenas o painel disponível,
        // evitando usar 100vh e criando scroll duplo.
        height: full ? "100%" : "calc(100vh - 120px)",
      }}
    >
      {/* Cliente - sempre aberto inicialmente */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" fontWeight="bold">
            Cliente
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <CustomerDetails
            selectedConversation={selectedConversation}
            onSelectedConversationUpdate={onSelectedConversationUpdate}
          />
        </AccordionDetails>
      </Accordion>

      {/* Detalhes da Solicitação - também aberto inicialmente */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" fontWeight="bold">
            {t("conversationDetails.request")}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <RequestDetails 
            key={selectedConversation?.id || "default"}
            selectedConversation={selectedConversation}
            onSelectedConversationUpdate={onSelectedConversationUpdate}
            onRefetchConversationDetails={onRefetchConversationDetails}
          />
        </AccordionDetails>
      </Accordion>

      {/* Insights da IA - sempre inicia FECHADO e carrega ao abrir */}
      <Accordion
        expanded={insightsExpanded}
        onChange={(_, expanded) => {
          setInsightsExpanded(expanded);
          if (expanded && onLoadAiInsights && selectedConversation) {
            onLoadAiInsights(selectedConversation.id);
          }
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" fontWeight="bold">
            {t("conversationDetails.aiInsights")}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <AiInsights
            insights={aiInsights || selectedConversation?.aiInsights}
            isLoading={isLoadingInsights}
            error={aiInsightsError}
            onGenerate={
              onLoadAiInsights && selectedConversation
                ? () => onLoadAiInsights(selectedConversation.id)
                : undefined
            }
          />
        </AccordionDetails>
      </Accordion>

      {/* Passagens (BPe) – visível apenas para segmento rodoviário + permissão bpe.view */}
      {canShowBPe && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6" fontWeight="bold">
              Passagens
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <PassagensBPe selectedConversation={selectedConversation} />
          </AccordionDetails>
        </Accordion>
      )}

      {canShowProducts && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6" fontWeight="bold">
              {t("conversationDetails.products")}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Products
              selectedConversation={selectedConversation}
              onRefetchConversationDetails={onRefetchConversationDetails}
            />
          </AccordionDetails>
        </Accordion>
      )}

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" fontWeight="bold">
            {t("conversationDetails.history")}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <History
            history={selectedConversation?.history}
            handleItemClick={handleItemClick}
            formatDate={formatDate}
          />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default ConversationDetails;
